import * as React from "react";
import * as ReactDOM from "react-dom";
import { Route } from "react-router-dom";
import { IComboBoxValue } from "../../../models/form";
import * as modelsGet from "../../../models/get";
import * as modelsPost from "../../../models/post";
import * as utils from "../../../utils/utils";
import * as apiCalendars from "../../../api/calendars";
import * as apiSubjects from "../../../api/subjects";
import { Window } from "../../../components/window";
import { Loader, LoaderStyle } from "../../../components/loader";
import { Button, ButtonType } from "../../../components/button";
import { ScrollViewer, ScrollBarVisibility } from "../../../components/scrollViewer";
import { FormGroup } from "../../../components/form";
import { ComboBox, ComboBoxType, DROPDOWN_TEXT_ITEM_HEIGHT, DropDownTextItem } from "../../../components/comboBox";
import { InputMask, InputSize } from "../../../components/input";
import { InputValueType } from "../../../enumerations";
import { TextBox } from "../../../components/textBox";
import { Alert, AlertType } from "../../../components/alert";

enum Mode
{
    create,
    edit
}

interface IProperties
{
    /**
     * Die ID des zu öffnenden Datensatzes. (Ist leer wenn eine neuer Datensatz erstellt werden soll.)
     */
    subjectId?: number,

    /**
     * Wird aufgerufen sobald sich das Fenster schließt.
     */
    onClose?: (refresh?: boolean) => void
}

interface IState
{
    /**
     * Gibt an, ob Daten gerade geladen werden.
     */
    isLoading?: boolean,

    /**
     * Gibt den Modus an in dem sich der Dialog befindet.
     */
    mode?: Mode,

    /**
     * Wenn im Bearbeitungsmodus, dann ist diese Eigenschaft gefüllt.
     */
    subject?: modelsGet.ISubject,

    /**
     * Enthält alle eingerichteten Betrefftypen.
     */
    subjectTypes?: modelsGet.ISubjectType[],

    /**
     * Enthält alle eingerichteten Kalender.
     */
    calendars?: modelsGet.ICalendar[],

    /**
     * Enthält die Eingabedaten des Forms.
     */
    form?: {
        subject?: string,
        subjectType?: IComboBoxValue<string, number>,
        calendar?: IComboBoxValue<string, number>,
        setuptimeBefore?: number,
        setuptimeAfter?: number
    },

    /**
     * Enthält Serverseitige Fehlermeldungen.
     */
    errors?: Object,

    /**
     * Der Text der Meldungen welche angezeigt werden soll.
     */
    alertMessage?: string,

    /**
     * Der Typ der Meldung.
     */
    alertType?: AlertType
}

export default class Dialog extends React.Component<IProperties, IState>
{
    private _initialState: IState;
    private _isSaveButtonDisabled: boolean;

    /**
     * Erstellt eine neue Instanz der Klasse @see Dialog.
     * @param props Die Eigenschaften dieser Komponente.
     */
    constructor(props: IProperties)
    {
        super(props);

        // Modus ermitteln.
        var mode: Mode = Mode.create;
        if (props.subjectId > 0)
        {
            mode = Mode.edit;
        }

        this.state = {
            isLoading: true,
            mode: mode,
            form: {
                subjectType: {},
                calendar: {}
            },
            errors: {}
        };

        this.initAsync(props);
    }

    /**
     * Lädt alle benötigten Daten und/oder weißt diese zu.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initAsync(props: IProperties)
    {
        if (this.state.mode === Mode.edit)
        {
            await this.initEditModeAsync(props);
        }
        else if (this.state.mode === Mode.create)
        {
            await this.initCreateModeAsync(props);
        }
    }

    /**
     * Initialisert den Edit Mode.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initEditModeAsync(props: IProperties)
    {
        // Betreff aus der Datenbank laden.
        var subject: modelsGet.ISubject = await apiSubjects.getSubject(props.subjectId);

        // Alle eingerichteten Betrefftypen ermitteln.
        var subjectTypes = await apiSubjects.getSubjectTypes();

        // Alle eingerichteten Kalender ermitteln.
        var calendars = await apiCalendars.getCalendars();

        // Werte setzen.
        this.setState((state: IState) =>
        {
            state.isLoading = false;
            state.errors = {};
            state.alertMessage = null;
            state.alertType = null;
            state.subject = subject;
            state.subjectTypes = subjectTypes;
            state.calendars = calendars;

            state.form.subject = subject.subject;
            state.form.subjectType = {
                value: subject.subject_type,
                selectedValue: subject.subject_type_id
            };
            state.form.calendar = {
                value: subject.calendar_name,
                selectedValue: subject.calendar_id
            };
            state.form.setuptimeBefore = subject.setuptime_before;
            state.form.setuptimeAfter = subject.setuptime_after;

            return state;
        }, () => this._initialState = {
            ...this.state,
            form: {
                ...this.state.form
            }
        });
    }

    /**
     * Initialisert den Create Mode.
     * @param props Alle gesetzten Eigenschaften.
     */
    private async initCreateModeAsync(props: IProperties)
    {
        // Alle eingerichteten Betrefftypen ermitteln.
        var subjectTypes = await apiSubjects.getSubjectTypes();

        // Alle eingerichteten Kalender ermitteln.
        var calendars = await apiCalendars.getCalendars();

        // Neues State Objekt erstellen.
        this.setState(() =>
        {
            const newState: IState = {};
            newState.isLoading = false;
            newState.errors = {};
            newState.alertMessage = null;
            newState.alertType = null;
            newState.subjectTypes = subjectTypes;
            newState.calendars = calendars;

            newState.form = {
                subjectType: {},
                calendar: {}
            };

            return newState;
        }, () => this._initialState = {
            ...this.state,
            form: {
                ...this.state.form
            }
        });
    }

    /**
     * Wird ausgeführt, bevor gerendert wird.
     * Hier wird geprüft ob der Button zum erstellen oder speichern
     * freigschaltet werden soll.
     * @param nextProps Die neuen Eigenschaften.
     * @param nextState Der neue State.
     */
    componentWillUpdate(nextProps: IProperties, nextState: IState)
    {
        this._isSaveButtonDisabled = true;

        if (this._initialState != null)
        {
            const { form } = this._initialState;

            // Betreff prüfen.
            if (form.subject !== nextState.form.subject)
                this._isSaveButtonDisabled = false;

            // Betrefftyp prüfen.
            if (form.subjectType.selectedValue !== nextState.form.subjectType.selectedValue)
                this._isSaveButtonDisabled = false;

            // Kalender prüfen.
            if (form.calendar.selectedValue !== nextState.form.calendar.selectedValue)
                this._isSaveButtonDisabled = false;

            // "Rüstzeit davor" prüfen.
            if (form.setuptimeBefore !== nextState.form.setuptimeBefore)
                this._isSaveButtonDisabled = false;

            // "Rüstzeit danach" prüfen.
            if (form.setuptimeAfter !== nextState.form.setuptimeAfter)
                this._isSaveButtonDisabled = false;
        }
    }

    /**
     * Wird ausgeführt, bevor der Dialog sich schließt.
     */
    private onClosing = (refresh: boolean = false) =>
    {
        var closeDialog: boolean = true;

        if (!this._isSaveButtonDisabled)
            closeDialog = confirm("Es sind noch ungespeicherte Änderungen vorhanden. Wollen Sie den Dialog trotzdem schließen?");

        if (closeDialog)
        {
            const { onClose } = this.props;
            onClose && onClose(refresh);
            return true;
        }
    }

    /**
     * Ändert den geöffneten Datensatz oder erstellt einen neuen.
     */
    private onSaveOrCreateClick = async () =>
    {
        try
        {
            this.setState({
                isLoading: true
            });

            const { mode, subject, form } = this.state;

            // Neues POST Objekt erstellen.
            var newData: modelsPost.ISubject = {};
            newData.calendar_id = form.calendar.selectedValue;
            newData.subject_type_id = form.subjectType.selectedValue;
            newData.subject = form.subject;
            newData.setuptime_before = form.setuptimeBefore;
            newData.setuptime_after = form.setuptimeAfter;

            if (mode === Mode.edit)
            {
                await apiSubjects.put(subject.id, newData);
            }
            else
            {
                await apiSubjects.post(newData);
            }

            // Fenster schließen.
            this._isSaveButtonDisabled = true;
            this.onClosing(true);
        }
        catch (e)
        {
            var errors = utils.getErrors(e);
            var alertMessage: string;

            if (errors != null)
            {
                for (let error in errors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                            alertMessage = errors[error];
                            break;
                        case "internal_server_error":
                            alertMessage = "Auf dem Server ist ein Fehler aufgetreten! Wenn der Fehler weiterhin auftritt, kontaktieren Sie Ihren Administrator oder Ihren Software Anbieter.";
                            break;
                    }
                }
            }

            this.setState(() =>
            {
                const newState: IState = {};
                newState.isLoading = false;
                newState.errors = errors;
                newState.alertMessage = alertMessage;
                newState.alertType = AlertType.error;
                return newState;
            });
        }
    }

    /**
     * Löscht den geöffneten Datensatz.
     */
    private onDeleteClick = async () =>
    {
        var deleteData = confirm("Wollen Sie den Datensatz wirklich löschen?");
        if (!deleteData)
            return;

        try
        {
            this.setState({
                isLoading: true
            });

            const { subject } = this.state;

            await apiSubjects.remove(subject.id);

            // Fenster schließen.
            this._isSaveButtonDisabled = true;
            this.onClosing(true);
        }
        catch (e)
        {
            var errors = utils.getErrors(e);
            var alertMessage: string;

            if (errors != null)
            {
                for (let error in errors)
                {
                    switch (error)
                    {
                        case "error":
                        case "date_is_invalid":
                            alertMessage = errors[error];
                            break;
                        case "internal_server_error":
                            alertMessage = "Auf dem Server ist ein Fehler aufgetreten! Wenn der Fehler weiterhin auftritt, kontaktieren Sie Ihren Administrator oder Ihren Software Anbieter.";
                            break;
                    }
                }
            }

            this.setState(() =>
            {
                const newState: IState = {};
                newState.isLoading = false;
                newState.errors = errors;
                newState.alertMessage = alertMessage;
                newState.alertType = AlertType.error;
                return newState;
            });
        }
    }

    /**
     * Wird ausgeführt, sobald sich der Wert eines Controls ändert.
     * @param value Der konvertierte Wert der TextBox.
     * @param originalValue Der originale Wert der TextBox.
     * @param name Der Name des Form Elements.
     */
    private onControlTextChanged = (value: any[], originalValue: string[], name: string) =>
    {
        this.setState((state: IState) =>
        {
            state.form[name] = value[0];
            return state;
        });
    }

    /**
     * Wird ausgeführt, sobald sich der Wert der SubjectType ComboBox ändert.
     * @param selectedItem Der ausgewählte Datensatz.
     */
    private onSubjectTypeSelectionChanged = (selectedItem: modelsGet.ISubjectType) =>
    {
        this.setState((state: IState) =>
        {
            state.form.subjectType = {
                value: selectedItem ? selectedItem.type_name : null,
                selectedValue: selectedItem ? selectedItem.id : null
            };
            return state;
        });
    }

    /**
     * Wird ausgeführt, sobald sich der Wert der Calendar ComboBox ändert.
     * @param selectedItem Der ausgewählte Datensatz.
     */
    private onCalendarSelectionChanged = (selectedItem: modelsGet.ICalendar) =>
    {
        this.setState((state: IState) =>
        {
            state.form.calendar = {
                value: selectedItem ? selectedItem.name : null,
                selectedValue: selectedItem ? selectedItem.id : null
            };
            return state;
        });
    }

    /**
     * Rendert das Formular/den Content Bereich.
     */
    private renderContent(): JSX.Element
    {
        const {
            mode,
            subject,
            subjectTypes,
            calendars,
            form,
            errors,
            alertMessage,
            alertType
        } = this.state;

        return (
            <div className="settings_dialog_content">
                <ScrollViewer
                    HorizontalScrollBarVisibility={ScrollBarVisibility.hidden}
                    VerticalScrollBarVisibility={ScrollBarVisibility.auto}
                    style={{
                        padding: 10
                    }}>
                    <FormGroup>
                        <TextBox
                            label="Anzeigetext"
                            value={form.subject}
                            size={InputSize.normal}
                            spellCheck={true}
                            autoFocus={true}
                            errorMessage={errors["Subject"]}
                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "subject")} />
                    </FormGroup>
                    <FormGroup>
                        <ComboBox
                            label="Betrefftyp"
                            canRemoveSelection={true}
                            dataSource={subjectTypes}
                            dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                            comboBoxType={ComboBoxType.dropDownList}
                            value={form.subjectType.value}
                            selectedValue={form.subjectType.selectedValue}
                            onSelectionChanged={this.onSubjectTypeSelectionChanged}
                            valueMember="id"
                            displayMember="type_name"
                            size={InputSize.normal}
                            errorMessage={errors["SubjectType"]}>
                            {
                                (item: modelsGet.ISubjectType) =>
                                {
                                    return (
                                        <DropDownTextItem
                                            key={item.id}
                                            value={item.id}
                                            displayValue={item.type_name} />
                                    );
                                }
                            }
                        </ComboBox>
                    </FormGroup>
                    <FormGroup>
                        <ComboBox
                            label="Kalender"
                            canRemoveSelection={true}
                            dataSource={calendars}
                            dropDownMenuItemHeight={DROPDOWN_TEXT_ITEM_HEIGHT}
                            comboBoxType={ComboBoxType.dropDownList}
                            value={form.calendar.value}
                            selectedValue={form.calendar.selectedValue}
                            onSelectionChanged={this.onCalendarSelectionChanged}
                            valueMember="id"
                            displayMember="name"
                            size={InputSize.normal}
                            errorMessage={errors["Calendar"]}>
                            {
                                (item: modelsGet.ICalendar) =>
                                {
                                    return (
                                        <DropDownTextItem
                                            key={item.id}
                                            value={item.id}
                                            displayValue={item.name} />
                                    );
                                }
                            }
                        </ComboBox>
                    </FormGroup>
                    <FormGroup isHorizontal={true}>
                        <TextBox
                            label="Rüstzeit vor einem Termin"
                            value={form.setuptimeBefore && form.setuptimeBefore.toString()}
                            valueType={InputValueType.number}
                            mask={InputMask.number}
                            size={InputSize.normal}
                            errorMessage={errors["SetuptimeBefore"]}
                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "setuptimeBefore")} />
                        <TextBox
                            label="Rüstzeit nach einem Termin"
                            value={form.setuptimeAfter && form.setuptimeAfter.toString()}
                            valueType={InputValueType.number}
                            mask={InputMask.number}
                            size={InputSize.normal}
                            errorMessage={errors["SetuptimeAfter"]}
                            onTextChange={(value, originalValue) => this.onControlTextChanged(value, originalValue, "setuptimeAfter")} />
                    </FormGroup>
                </ScrollViewer>
            </div>
        );
    }

    /**
     * Rendert diese Komponente.
     */
    public render(): JSX.Element
    {
        const {
            isLoading,
            mode,
            subject,
            form,
            errors,
            alertMessage,
            alertType
        } = this.state;

        var icon: string = "loading";
        var title: string = "Daten werden geladen...";
        var buttonHeader: string = "";

        if (!isLoading)
        {
            if (mode === Mode.create)
            {
                icon = "plus";
                title = "Neuen Betreff erstellen";
                buttonHeader = "Erstellen";
            }
            else
            {
                icon = "pencil";
                title = `Betreff "${subject.subject}" bearbeiten`;
                buttonHeader = "Speichern";
            }
        }

        return (
            <Window
                isOpen={true}
                titleIcon={icon}
                title={title}
                onClosing={this.onClosing}
                isMaximized={false}
                width={600}
                height={560}
                onEnterKeyUp={this.onSaveOrCreateClick}
                onEscapeKeyUp={() => this.onClosing(false)}>
                <div className="settings_dialog">
                    <Alert
                        style={utils.getGridChildStyle(1, 1, 1, 1)}
                        alertType={alertType}
                        isVisible={alertMessage ? true : false}
                        isClosable={true}
                        onHide={() => this.setState({ alertMessage: null, alertType: null })}>
                        {alertMessage}
                    </Alert>
                    {isLoading ? <Loader loaderStyle={LoaderStyle.style1} /> : this.renderContent()}
                    <div className="settings_dialog_actions">
                        {
                            mode === Mode.edit &&
                            <Button
                                text="Löschen"
                                type={ButtonType.danger}
                                onClick={this.onDeleteClick} />
                        }
                        <div style={{ flexGrow: 1 }}></div>
                        <Button
                            text={buttonHeader}
                            type={ButtonType.primary}
                            isDisabled={this._isSaveButtonDisabled}
                            onClick={this.onSaveOrCreateClick} />
                        <Button
                            text="Abbrechen"
                            type={ButtonType.default}
                            onClick={() => this.onClosing(false)} />
                    </div>
                </div>
            </Window>
        );
    }
}